import { useTranslation } from 'react-i18next';
import { useParams, Navigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { instance } from '~/utils/api/api';
import { useToastError } from '~/utils/useToastError';
import { useToastSuccess } from '~/utils/useToastSuccess';
import { useToastWarning } from '~/utils/useToastWarning';
import Heading from '~/components/Shared/Heading/Heading';
import Page from '~/components/Shared/Layout/Page/Page';
import { Pagination } from '~/ui';
import { useState, useEffect, useCallback, useRef } from 'react';
import TrashIcon from '~/assets/svg/newSvg/basket.svg?react';
import EmptyImageIcon from '~/assets/svg/newSvg/empty-image.svg?react';
import PhotoIcon from '~/assets/svg/newSvg/picture.svg?react';
import ArrowLeftIcon from '~/assets/svg/newSvg/arrow-light-left.svg?react';
import ArrowRightIcon from '~/assets/svg/newSvg/arrow-light-right.svg?react';
import { Modal } from '~/components/Shared/Modal/Modal';
import { Portal } from '~/components/Shared/Portal/Portal';
import CloseIcon from '~/assets/svg/newSvg/cross-close.svg?react';
import styles from './PhotoStoragePhotos.module.scss';

interface IPhoto {
  id: number;
  photo_id: number;
  asset_id: number;
  module: string;
  url: string;
}

const ALLOWED_MIME_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];

const PhotoStoragePhotosRedirectGuard = () => {
  const { module, assetId, entityId } = useParams<{ module: string; assetId: string; entityId?: string }>();
  if ((module === 'accommodation' || module === 'food') && !entityId) {
    return <Navigate to={`/documents/photo-storage/${module}/${assetId}/entities`} replace />;
  }
  return <PhotoStoragePhotos />;
};

export default PhotoStoragePhotosRedirectGuard;

const PhotoStoragePhotos = () => {
  const { t } = useTranslation();
  const { module, assetId, entityId } = useParams<{ module: string; assetId: string; entityId?: string }>();

  const toastError = useToastError();
  const toastSuccess = useToastSuccess();
  const toastWarning = useToastWarning();
  const client = useQueryClient();
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(50);
  const [displayedPhoto, setDisplayedPhoto] = useState<IPhoto | null>(null);
  const [incomingPhoto, setIncomingPhoto] = useState<IPhoto | null>(null);
  const [previewBroken, setPreviewBroken] = useState(false);
  const [navDir, setNavDir] = useState<'left' | 'right'>('right');
  const [animKey, setAnimKey] = useState(0);
  const [deleteConfirmId, setDeleteConfirmId] = useState<number | null>(null);
  const [loadedIds, setLoadedIds] = useState<Set<number>>(new Set());
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const progressTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const uploadInputRef = useRef<HTMLInputElement>(null);
  const dragCounterRef = useRef(0);

  const handlePageChange = (selectedPage: { selected: number }) => {
    setPage(selectedPage.selected + 1);
    setLoadedIds(new Set());
  };

  const handlePerPageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setPerPage(Number(e.target.value));
    setPage(1);
    setLoadedIds(new Set());
  };

  const moduleName = t(`photo_storage_${module}`);

  const { data: photos, isLoading } = useQuery({
    queryFn: async () => {
      const { data } = await instance.get(
        `admin/photo-storage/${module}/${assetId}`,
        { params: { perPage, page, ...(entityId ? { entity_id: entityId } : {}) } }
      );
      return data;
    },
    queryKey: ['photo-storage-photos', module, assetId, entityId, page, perPage],
    onError: error => toastError(error),
  });

  const photoList: IPhoto[] = photos?.data || [];

  const openPreview = (photo: IPhoto) => {
    setDisplayedPhoto(photo);
    setIncomingPhoto(null);
    setPreviewBroken(false);
    setAnimKey(k => k + 1);
  };

  const navigate = useCallback((dir: 1 | -1) => {
    if (!photoList.length) return;
    const base = incomingPhoto || displayedPhoto;
    if (!base) return;
    const idx = photoList.findIndex(p => p.id === base.id);
    const next = photoList[(idx + dir + photoList.length) % photoList.length];
    setNavDir(dir === 1 ? 'right' : 'left');
    setPreviewBroken(false);
    setIncomingPhoto(next);
  }, [displayedPhoto, incomingPhoto, photoList]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!displayedPhoto) return;
      if (e.key === 'ArrowLeft') navigate(-1);
      if (e.key === 'ArrowRight') navigate(1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [displayedPhoto, navigate]);

  useEffect(() => {
    if (isUploading) {
      setUploadProgress(0);
      progressTimerRef.current = setInterval(() => {
        setUploadProgress(prev => {
          if (prev >= 85) {
            if (progressTimerRef.current) clearInterval(progressTimerRef.current);
            return 85;
          }
          return prev + Math.random() * 8 + 3;
        });
      }, 300);
    } else {
      if (progressTimerRef.current) clearInterval(progressTimerRef.current);
      if (uploadProgress > 0) {
        setUploadProgress(100);
        setTimeout(() => setUploadProgress(0), 600);
      }
    }
    return () => {
      if (progressTimerRef.current) clearInterval(progressTimerRef.current);
    };
  }, [isUploading]);

  const { mutateAsync: uploadPhotos } = useMutation(
    async (files: File[]) => {
      const formData = new FormData();
      files.forEach(file => formData.append('photos[]', file));
      if (entityId) formData.append('entity_id', entityId);
      const { data } = await instance.post(
        `admin/photo-storage/${module}/${assetId}`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      return data;
    },
    {
      onSuccess: () => {
        toastSuccess(t('photo_uploaded'));
        client.invalidateQueries(['photo-storage-photos']);
        client.invalidateQueries(['photo-storage-assets']);
        client.invalidateQueries(['photo-storage-modules']);
      },
      onError: error => toastError(error),
    }
  );

  const handleUploadFiles = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files?.length) return;
    setIsUploading(true);
    try {
      await uploadPhotos(Array.from(files));
    } finally {
      setIsUploading(false);
      if (uploadInputRef.current) uploadInputRef.current.value = '';
    }
  };

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    dragCounterRef.current += 1;
    if (dragCounterRef.current === 1) setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    dragCounterRef.current -= 1;
    if (dragCounterRef.current === 0) setIsDragging(false);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    dragCounterRef.current = 0;
    setIsDragging(false);
    const all = Array.from(e.dataTransfer.files);
    const files = all.filter(f => ALLOWED_MIME_TYPES.includes(f.type));
    if (all.length > files.length) {
      toastWarning(t('only_images_allowed'));
    }
    if (!files.length) return;
    setIsUploading(true);
    try {
      await uploadPhotos(files);
    } finally {
      setIsUploading(false);
    }
  };

  const { mutateAsync: deletePhoto } = useMutation(
    async (id: number) => {
      await instance.delete(`admin/photo-storage/${id}`);
    },
    {
      onSuccess: () => {
        toastSuccess(t('deleted_successfully'));
        client.invalidateQueries(['photo-storage-photos']);
        client.invalidateQueries(['photo-storage-assets']);
        client.invalidateQueries(['photo-storage-modules']);
        setDeleteConfirmId(null);
      },
      onError: error => toastError(error),
    }
  );

  return (
    <Page heading={<Heading text={`${t('photo_storage')} — ${moduleName}`} />}>
      <div
        className={styles.container}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
      >
        {isDragging && (
          <div className={styles.dropOverlay}>
            <div className={styles.dropOverlayCard}>
              <div className={styles.dropOverlayIconWrap}>
                <PhotoIcon className={styles.dropOverlayIcon} />
              </div>
              <p className={styles.dropOverlayText}>{t('drop_photos_here')}</p>
              <p className={styles.dropOverlayHint}>{t('drop_photos_hint')}</p>
            </div>
          </div>
        )}
        <input
          ref={uploadInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/gif"
          multiple
          onChange={handleUploadFiles}
          className={styles.hiddenInput}
        />
        {!isLoading && !!photos?.data?.length ? (
          <div className={styles.toolbar}>
            {isUploading ? (
              <div className={styles.uploadProgress}>
                <div className={styles.uploadProgressHeader}>
                  <span className={styles.uploadProgressLabel}>
                    <PhotoIcon className={styles.uploadProgressIcon} />
                    {t('upload_photos')}
                  </span>
                  <span className={styles.uploadProgressPercent}>
                    {Math.min(Math.round(uploadProgress), 100)}%
                  </span>
                </div>
                <div className={styles.uploadProgressTrack}>
                  <div
                    className={styles.uploadProgressFill}
                    style={{ width: `${Math.min(Math.round(uploadProgress), 100)}%` }}
                  />
                </div>
              </div>
            ) : (
              <button
                type="button"
                className={styles.uploadButton}
                onClick={() => uploadInputRef.current?.click()}
              >
                <PhotoIcon className={styles.uploadButtonIcon} />
                {t('upload_photos')}
              </button>
            )}
          </div>
        ) : null}

        {isLoading ? (
          <div className={styles.grid}>
            {Array.from({ length: perPage }).map((_, i) => (
              <div key={i} className={styles.photoCard} style={{ animationDelay: `${Math.min(i * 20, 400)}ms` }}>
                <div className={styles.shimmer} />
              </div>
            ))}
          </div>
        ) : (
          <>
            {!photos?.data?.length ? (
              <div className={styles.emptyState}>
                <div className={styles.emptyIcon}>
                  <PhotoIcon className={styles.emptyPhotoIcon} />
                </div>
                <p className={styles.emptyTitle}>{t('no_photos_yet')}</p>
                <p className={styles.emptySubtitle}>{t('upload_first_photo')}</p>
                {isUploading ? (
                  <div className={styles.emptyUploadProgress}>
                    <div className={styles.emptyProgressHeader}>
                      <span className={styles.emptyProgressLabel}>
                        <PhotoIcon className={styles.emptyProgressIcon} />
                        {t('upload_photos')}
                      </span>
                      <span className={styles.emptyProgressPercent}>
                        {Math.min(Math.round(uploadProgress), 100)}%
                      </span>
                    </div>
                    <div className={styles.emptyProgressTrack}>
                      <div
                        className={styles.emptyProgressFill}
                        style={{ width: `${Math.min(Math.round(uploadProgress), 100)}%` }}
                      />
                    </div>
                  </div>
                ) : (
                  <button
                    type="button"
                    className={styles.emptyUploadButton}
                    onClick={() => uploadInputRef.current?.click()}
                  >
                    <PhotoIcon className={styles.emptyUploadIcon} />
                    {t('upload_photos')}
                  </button>
                )}
              </div>
            ) : (
              <>
                <div className={styles.grid}>
                  {(photos?.data || []).map((photo: IPhoto, i: number) => (
                    <div key={photo.id} className={styles.photoCard} style={{ animationDelay: `${Math.min(i * 30, 300)}ms` }}>
                      {!loadedIds.has(photo.id) && (
                        <div className={styles.shimmer} />
                      )}
                      <button
                        type="button"
                        className={styles.photoButton}
                        onClick={() => openPreview(photo)}
                      >
                        <img
                          src={photo.url}
                          alt=""
                          className={styles.photoImage}
                          onLoad={() =>
                            setLoadedIds(prev => new Set(prev).add(photo.id))
                          }
                          onError={e => {
                            setLoadedIds(prev => new Set(prev).add(photo.id));
                            (e.currentTarget as HTMLImageElement).style.display = 'none';
                            e.currentTarget.nextElementSibling?.classList.add(styles.brokenVisible);
                          }}
                        />
                        <div className={styles.brokenPlaceholder}>
                          <EmptyImageIcon className={styles.brokenIcon} />
                          <span className={styles.brokenText}>{t('failed_to_load')}</span>
                        </div>
                      </button>
                      <button
                        type="button"
                        className={styles.deleteButton}
                        onClick={() => setDeleteConfirmId(photo.id)}
                      >
                        <TrashIcon className={styles.deleteIcon} />
                      </button>
                    </div>
                  ))}
                </div>
                <div className={styles.paginationWrapper}>
                  <label className={styles.perPageWrapper}>
                    <span className={styles.perPageLabel}>{t('per_page')}</span>
                    <div className={styles.perPageSelectWrap}>
                      <select
                        className={styles.perPageSelect}
                        value={perPage}
                        onChange={handlePerPageChange}
                      >
                        {[10, 20, 50, 100].map(n => (
                          <option key={n} value={n}>{n}</option>
                        ))}
                      </select>
                      <svg className={styles.perPageChevron} viewBox="0 0 10 6" fill="none">
                        <path d="M1 1l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                      </svg>
                    </div>
                  </label>
                  <Pagination
                    pageCount={photos?.last_page || 1}
                    forcePage={page}
                    onPageChange={handlePageChange}
                  />
                </div>
              </>
            )}
          </>
        )}
      </div>

      {/* Просмотр фото — fullscreen */}
      {displayedPhoto && (
        <Portal>
          <div className={styles.lightbox} onClick={() => setDisplayedPhoto(null)}>
            {/* Размытый фон — обновляется только когда incoming загружен */}
            <div
              className={styles.lightboxBg}
              style={{ backgroundImage: `url(${displayedPhoto.url})` }}
            />
            <div className={styles.lightboxDim} />

            {/* Скрытый preloader — грузит следующее фото */}
            {incomingPhoto && (
              <img
                key={`preload-${incomingPhoto.id}`}
                src={incomingPhoto.url}
                style={{ display: 'none' }}
                onLoad={() => {
                  setDisplayedPhoto(incomingPhoto);
                  setIncomingPhoto(null);
                  setAnimKey(k => k + 1);
                }}
                onError={() => {
                  setDisplayedPhoto(incomingPhoto);
                  setIncomingPhoto(null);
                  setPreviewBroken(true);
                  setAnimKey(k => k + 1);
                }}
              />
            )}

            {/* Кнопка закрыть */}
            <button
              type="button"
              className={styles.lightboxClose}
              onClick={() => setDisplayedPhoto(null)}
            >
              <CloseIcon className={styles.lightboxCloseIcon} />
            </button>

            {/* Контент */}
            <div className={styles.lightboxContent} onClick={e => e.stopPropagation()}>
              <button
                type="button"
                className={`${styles.lightboxNav} ${incomingPhoto ? styles.lightboxNavLoading : ''}`}
                onClick={() => navigate(-1)}
              >
                <ArrowLeftIcon className={styles.lightboxNavIcon} />
              </button>

              <div className={styles.lightboxImageWrapper}>
                {!previewBroken ? (
                  <img
                    key={animKey}
                    src={displayedPhoto.url}
                    alt=""
                    className={`${styles.lightboxImage} ${navDir === 'right' ? styles.slideFromRight : styles.slideFromLeft}`}
                  />
                ) : (
                  <div className={styles.lightboxBroken}>
                    <EmptyImageIcon className={styles.lightboxBrokenIcon} />
                    <span className={styles.lightboxBrokenText}>{t('failed_to_load')}</span>
                  </div>
                )}
              </div>

              <button
                type="button"
                className={`${styles.lightboxNav} ${incomingPhoto ? styles.lightboxNavLoading : ''}`}
                onClick={() => navigate(1)}
              >
                <ArrowRightIcon className={styles.lightboxNavIcon} />
              </button>
            </div>

            {/* Счётчик */}
            <div className={styles.lightboxCounter}>
              {incomingPhoto && <span className={styles.lightboxSpinner} />}
              {photoList.findIndex(p => p.id === displayedPhoto.id) + 1} / {photoList.length}
            </div>
          </div>
        </Portal>
      )}

      {/* Подтверждение удаления */}
      <Modal
        isOpen={deleteConfirmId !== null}
        onClose={() => setDeleteConfirmId(null)}
      >
        <div className={styles.confirmWrapper}>
          <p className={styles.confirmTitle}>{t('sure_delete_file')}</p>
          <p className={styles.confirmDescription}>
            {t('photo_will_be_deleted_everywhere')}
          </p>
          <div className={styles.confirmButtons}>
            <button
              type="button"
              className={styles.cancelButton}
              onClick={() => setDeleteConfirmId(null)}
            >
              {t('cancel')}
            </button>
            <button
              type="button"
              className={styles.confirmDeleteButton}
              onClick={() => deleteConfirmId && deletePhoto(deleteConfirmId)}
            >
              {t('delete')}
            </button>
          </div>
        </div>
      </Modal>
    </Page>
  );
};

